import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import Modal from "@/components/Modal";
import ImageInput from "@/components/ImageInput";
import TeamDetailModal from "@/components/TeamDetailModal";
import { brandLogoFor } from "@/pages/overlays/_shared";
import { Plus, Trophy, Trash, UserPlus, Copy, PencilSimple } from "@phosphor-icons/react";
import { resolveImageUrl } from "@/components/ImageInput";
import { toast } from "sonner";

const COLORS = ["#007AFF", "#FF3B30", "#00FF41", "#F59E0B", "#A855F7", "#EC4899", "#14B8A6", "#EF4444"];

function TeamForm({ auction, initial, onSave, formId, isEdit }) {
  const [form, setForm] = useState({
    name: initial?.name || "",
    owner_name: initial?.owner_name || "",
    logo_url: initial?.logo_url || "",
    color: initial?.color || "#007AFF",
    budget: initial?.budget || auction.settings?.default_team_budget || 1000,
    hotkey: initial?.hotkey || "",
  });
  const submit = async (e) => {
    e.preventDefault();
    await onSave({
      name: form.name,
      owner_name: form.owner_name,
      logo_url: form.logo_url || undefined,
      color: form.color,
      budget: Number(form.budget),
      hotkey: form.hotkey ? form.hotkey.toLowerCase().trim().slice(0, 3) : undefined,
    });
  };
  return (
    <form id={formId} onSubmit={submit} data-testid="team-form" className="space-y-5">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="label-mono block mb-2">Team Name*</label>
          <input required data-testid="input-team-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full bg-white border border-gray-200 rounded px-3 py-2 focus:border-blue-500 outline-none" />
        </div>
        <div>
          <label className="label-mono block mb-2">Owner Name*</label>
          <input required data-testid="input-owner-name" value={form.owner_name} onChange={(e) => setForm({ ...form, owner_name: e.target.value })} className="w-full bg-white border border-gray-200 rounded px-3 py-2 focus:border-blue-500 outline-none" />
        </div>
        <div>
          <label className="label-mono block mb-2">Budget (points)</label>
          <input type="number" min="1" data-testid="input-team-budget" value={form.budget} onChange={(e) => setForm({ ...form, budget: e.target.value })} className="w-full bg-white border border-gray-200 rounded px-3 py-2 focus:border-blue-500 outline-none" />
        </div>
        <div>
          <label className="label-mono block mb-2">Quick-Bid Hotkey <span className="text-gray-400 normal-case tracking-normal font-normal">(single letter or key label — press on Live Auction to place next-min bid)</span></label>
          <input
            type="text"
            maxLength={3}
            placeholder="e.g.  q  or  1  or  f1"
            data-testid="input-team-hotkey"
            value={form.hotkey}
            onChange={(e) => setForm({ ...form, hotkey: e.target.value })}
            className="w-full bg-white border border-gray-200 rounded px-3 py-2 focus:border-blue-500 outline-none uppercase font-mono tracking-widest"
          />
        </div>
        <div>
          <label className="label-mono block mb-2">Team Color</label>
          <div className="flex flex-wrap gap-2">
            {COLORS.map((c) => (
              <button type="button" key={c} onClick={() => setForm({ ...form, color: c })} className={`w-9 h-9 rounded ${form.color === c ? "ring-2 ring-white ring-offset-2 ring-offset-white" : ""}`} style={{ background: c }} />
            ))}
          </div>
        </div>
        <div className="sm:col-span-2">
          <ImageInput
            label="Team Logo"
            value={form.logo_url}
            onChange={(v) => setForm({ ...form, logo_url: v })}
            testId="team-logo-input"
            maxKB={5120}
            aspect="aspect-square"
          />
        </div>
      </div>

      {!isEdit && (
        <div className="text-xs text-gray-500 border border-gray-200 rounded p-3 bg-slate-50/60">
          Default budget: <span className="text-emerald-600 font-mono">{auction.settings?.default_team_budget || 1000} pts</span> · edit above to override for this team.
        </div>
      )}
    </form>
  );
}

function InviteOwnerModal({ team, open, onClose, onDone }) {
  const [existing, setExisting] = useState(null);
  const [form, setForm] = useState({ email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    api.get(`/teams/${team.id}/owner`)
      .then(({ data }) => {
        setExisting(data);
        if (data) setForm({ email: data.email || "", password: "" });
        else setForm({ email: "", password: "" });
      })
      .catch(() => setExisting(null))
      .finally(() => setLoading(false));
  }, [open, team.id]);

  const save = async (e) => {
    e.preventDefault();
    if (!form.email || !form.password) { toast.error("Email and password are required"); return; }
    setBusy(true);
    try {
      await api.post(`/teams/${team.id}/owner`, { email: form.email, password: form.password, name: team.owner_name });
      toast.success("Team owner invited");
      onDone?.();
      onClose();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const remove = async () => {
    if (!window.confirm("Remove team-owner login for this team?")) return;
    setBusy(true);
    try {
      await api.delete(`/teams/${team.id}/owner`);
      toast.success("Owner removed");
      setExisting(null);
      setForm({ email: "", password: "" });
      onDone?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const loginUrl = `${window.location.origin}/login`;
  const copyCreds = async () => {
    const text = `NitiSoft Auction — ${team.name}\nLogin: ${loginUrl}\nEmail: ${form.email}\nPassword: ${form.password || "(the one you just set)"}`;
    try { await navigator.clipboard.writeText(text); toast.success("Copied to clipboard"); }
    catch (e) { toast.error("Copy failed"); }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={existing ? "Team Owner Login" : "Invite Team Owner"}
      subtitle={team.name}
      testId="invite-owner-modal"
      footer={
        <>
          {existing && (
            <button type="button" onClick={remove} disabled={busy} className="btn-ghost !border-red-500/40 !text-red-600 mr-auto" data-testid="remove-owner">
              Remove Owner
            </button>
          )}
          <button type="button" onClick={onClose} className="btn-ghost">Cancel</button>
          <button type="submit" form="invite-owner-form" disabled={busy} data-testid="submit-invite-owner" className="btn-primary">
            {busy ? "Saving…" : (existing ? "Update" : "Send Invite")}
          </button>
        </>
      }
    >
      {loading ? <div className="label-mono py-6 text-center">Loading…</div> : (
        <form id="invite-owner-form" onSubmit={save} className="space-y-4" data-testid="invite-owner-form">
          <p className="text-sm text-gray-500">
            Set a login for <span className="text-slate-900 font-semibold">{team.name}</span>&apos;s owner. Share the credentials — they can sign in and bid live from their own device.
          </p>

          <div>
            <label className="label-mono block mb-2">Owner Email*</label>
            <input required type="email" data-testid="invite-owner-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="owner@example.com" className="w-full bg-white border border-gray-200 rounded px-3 py-2 focus:border-blue-500 outline-none" />
          </div>
          <div>
            <label className="label-mono block mb-2">Password* {existing && <span className="text-gray-500 normal-case tracking-normal">(sets a new password)</span>}</label>
            <input required type="text" data-testid="invite-owner-password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="min 6 chars" minLength="6" className="w-full bg-white border border-gray-200 rounded px-3 py-2 focus:border-blue-500 outline-none" />
          </div>

          <div className="border border-gray-200 rounded p-3 bg-slate-50/60 space-y-2">
            <div className="label-mono">SHARE WITH OWNER</div>
            <div className="text-xs font-mono text-gray-600">
              <div>Login: <span className="text-blue-700">{loginUrl}</span></div>
              <div>Email: {form.email || "—"}</div>
              <div>Password: {form.password || "—"}</div>
            </div>
            <button type="button" onClick={copyCreds} className="btn-ghost text-xs !py-1 !px-2 flex items-center gap-1" data-testid="copy-invite">
              <Copy size={12} /> Copy credentials
            </button>
          </div>
        </form>
      )}
    </Modal>
  );
}


export default function TeamsTab({ auction }) {
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [busy, setBusy] = useState(false);
  const [inviteTeam, setInviteTeam] = useState(null);
  const [detailTeamId, setDetailTeamId] = useState(null);
  const [editTeam, setEditTeam] = useState(null);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get(`/auctions/${auction.id}/teams`); setTeams(data); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [auction.id]);

  const create = async (payload) => {
    setBusy(true);
    try { await api.post(`/auctions/${auction.id}/teams`, payload); toast.success("Team created"); setShowCreate(false); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const saveEdit = async (payload) => {
    if (!editTeam) return;
    setBusy(true);
    try {
      await api.patch(`/teams/${editTeam.id}`, payload);
      toast.success("Team updated");
      setEditTeam(null);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const del = async (t) => {
    if (!window.confirm(`Delete team ${t.name}?`)) return;
    try { await api.delete(`/teams/${t.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const teamLimit = Number(auction?.number_of_teams || 0);
  const limitReached = teamLimit > 0 && teams.length >= teamLimit;

  return (
    <div className="space-y-6" data-testid="teams-tab">
      <div className="flex justify-between items-center">
        <div className="label-mono">TEAMS · {teams.length}{teamLimit ? ` / ${teamLimit}` : ""}</div>
        <button
          onClick={() => setShowCreate(true)}
          disabled={limitReached}
          data-testid="create-team-button"
          title={limitReached ? `Team limit reached (${teams.length}/${teamLimit}). Upgrade the plan to add more.` : "Add a new team"}
          className="btn-primary text-xs flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <Plus size={14} /> {limitReached ? "Limit Reached" : "Create Team"}
        </button>
      </div>

      {loading ? (
        <div className="label-mono py-16 text-center">Loading…</div>
      ) : teams.length === 0 ? (
        <div className="border border-dashed border-gray-200 rounded p-10 text-center text-gray-500" data-testid="teams-empty">
          No teams yet. Create your first franchise for this auction.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {teams.map((t) => {
            const pct = t.budget ? Math.round(((t.budget - t.remaining_budget) / t.budget) * 100) : 0;
            return (
              <div key={t.id} className="bg-white border border-gray-200 rounded p-5 hover:border-gray-300 transition-colors" data-testid={`team-card-${t.id}`}>
                <div className="flex items-center gap-3 mb-4">
                  <button type="button" onClick={() => setDetailTeamId(t.id)} className="flex items-center gap-3 flex-1 min-w-0 text-left" data-testid={`open-team-${t.id}`}>
                    {t.logo_url ? (
                      <img src={resolveImageUrl(t.logo_url)} alt="" className="w-12 h-12 rounded object-cover border" style={{ borderColor: `${t.color}55` }} onError={(e) => { e.target.style.display = "none"; }} />
                    ) : (
                      <div className="w-12 h-12 grid place-items-center rounded" style={{ background: `${t.color}22`, border: `1px solid ${t.color}55` }}>
                        <Trophy weight="fill" size={22} style={{ color: t.color }} />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="font-display text-2xl leading-none truncate hover:text-emerald-600 transition-colors">{t.name}</div>
                      <div className="label-mono">{t.short_name} · {t.owner_name}</div>
                    </div>
                  </button>
                  <button onClick={() => setEditTeam(t)} className="p-2 rounded hover:bg-gray-100" data-testid={`edit-team-${t.id}`} title="Edit team">
                    <PencilSimple size={14} className="text-emerald-600" />
                  </button>
                  <button onClick={() => setInviteTeam(t)} className="p-2 rounded hover:bg-gray-100" data-testid={`invite-owner-${t.id}`} title="Invite team owner">
                    <UserPlus size={14} className="text-blue-600" />
                  </button>
                  <button onClick={() => del(t)} className="p-2 rounded hover:bg-red-950" data-testid={`delete-team-${t.id}`}>
                    <Trash size={14} className="text-red-600" />
                  </button>
                </div>
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div><div className="font-display text-2xl">{t.remaining_budget}</div><div className="label-mono">Left</div></div>
                  <div><div className="font-display text-2xl">{t.players_bought || 0}</div><div className="label-mono">Bought</div></div>
                  <div><div className="font-display text-2xl">{t.budget - t.remaining_budget}</div><div className="label-mono">Spent</div></div>
                </div>
                <div className="h-1 bg-gray-100 rounded mt-3 overflow-hidden"><div className="h-full" style={{ width: `${pct}%`, background: t.color }} /></div>
              </div>
            );
          })}
        </div>
      )}

      <Modal
        open={showCreate}
        onClose={() => setShowCreate(false)}
        title="Create Team"
        subtitle="NEW FRANCHISE"
        size="lg"
        testId="create-team-modal"
        footer={
          <>
            <button type="button" onClick={() => setShowCreate(false)} className="btn-ghost">Cancel</button>
            <button type="submit" form="team-form-el" disabled={busy} data-testid="submit-team" className="btn-primary">
              {busy ? "Creating…" : "Create Team"}
            </button>
          </>
        }
      >
        <TeamForm auction={auction} onSave={create} formId="team-form-el" />
      </Modal>

      {editTeam && (
        <Modal
          open={!!editTeam}
          onClose={() => setEditTeam(null)}
          title={`Edit ${editTeam.name}`}
          subtitle="TEAM DETAILS"
          size="lg"
          testId="edit-team-modal"
          footer={
            <>
              <button type="button" onClick={() => setEditTeam(null)} className="btn-ghost">Cancel</button>
              <button type="submit" form="edit-team-form-el" disabled={busy} data-testid="submit-edit-team" className="btn-primary">
                {busy ? "Saving…" : "Save Changes"}
              </button>
            </>
          }
        >
          <TeamForm
            auction={auction}
            initial={editTeam}
            onSave={saveEdit}
            formId="edit-team-form-el"
            isEdit
          />
        </Modal>
      )}

      {inviteTeam && (
        <InviteOwnerModal
          team={inviteTeam}
          open={!!inviteTeam}
          onClose={() => setInviteTeam(null)}
          onDone={load}
        />
      )}

      <TeamDetailModal
        teamId={detailTeamId}
        open={!!detailTeamId}
        onClose={() => setDetailTeamId(null)}
        brandLogoUrl={brandLogoFor(auction)}
      />
    </div>
  );
}
